import { useCallback, useEffect, useRef, useState } from 'react';

import clsxm from '@/lib/clsxm';

import { imageSrcSet, imageUrl, type LandingImage } from './assets';

interface CompareViewProps {
  before: LandingImage;
  after: LandingImage;
  beforeAlt: string;
  afterAlt: string;
  labels: { before: string; after: string };
  /** Accessible name of the divider. */
  sliderLabel: string;
  /** The hero's images: load first, at high priority. */
  eager?: boolean;
  /** `sizes` for the images' srcset. */
  sizes: string;
  /** Where the divider starts (0-100). */
  initial?: number;
  className?: string;
}

/** Divider swing that shows the view can be dragged (once, when first seen). */
const hintFrom = (start: number) => [
  { at: 0, value: start },
  { at: 550, value: Math.max(8, start - 20) },
  { at: 1250, value: Math.min(92, start + 12) },
  { at: 1800, value: start },
];

const easeInOut = (t: number) =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

const hintAt = (hint: ReturnType<typeof hintFrom>, elapsed: number) => {
  for (let i = 1; i < hint.length; i++) {
    const from = hint[i - 1];
    const to = hint[i];
    if (elapsed <= to.at) {
      const t = easeInOut((elapsed - from.at) / (to.at - from.at));
      return from.value + (to.value - from.value) * t;
    }
  }
  return null;
};

/**
 * Before / after of the same room: the "before" photo shows left of a
 * draggable divider. Works with mouse, touch and pen (pointer capture) and
 * with the keyboard (arrows, Home, End). Fills its positioned parent.
 */
export default function CompareView({
  before,
  after,
  beforeAlt,
  afterAlt,
  labels,
  sliderLabel,
  eager,
  sizes,
  initial = 50,
  className,
}: CompareViewProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const touched = useRef(false);
  const [position, setPosition] = useState(initial);
  const [dragging, setDragging] = useState(false);

  const moveTo = useCallback((clientX: number) => {
    const box = containerRef.current?.getBoundingClientRect();
    if (!box || box.width === 0) return;
    const next = ((clientX - box.left) / box.width) * 100;
    setPosition(Math.min(100, Math.max(0, next)));
  }, []);

  // A short swing of the divider the first time the view is on screen, so
  // people see they can drag it. Skipped for reduced motion.
  useEffect(() => {
    const node = containerRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    let frame = 0;
    let timer = 0;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        timer = window.setTimeout(() => {
          const hint = hintFrom(initial);
          const start = performance.now();
          const step = (now: number) => {
            if (touched.current) return;
            const value = hintAt(hint, now - start);
            if (value === null) return;
            setPosition(value);
            frame = requestAnimationFrame(step);
          };
          frame = requestAnimationFrame(step);
        }, 700);
      },
      { threshold: 0.6 },
    );
    observer.observe(node);
    return () => {
      observer.disconnect();
      window.clearTimeout(timer);
      cancelAnimationFrame(frame);
    };
  }, [initial]);

  const imageProps = {
    sizes,
    draggable: false,
    decoding: 'async' as const,
    loading: eager ? ('eager' as const) : ('lazy' as const),
    ...(eager ? { fetchPriority: 'high' as const } : {}),
  };

  return (
    <div
      ref={containerRef}
      // Images are physical left/right, also in right-to-left pages.
      dir='ltr'
      className={clsxm(
        'group absolute inset-0 touch-pan-y select-none overflow-hidden',
        dragging ? 'cursor-grabbing' : 'cursor-ew-resize',
        className,
      )}
      onPointerDown={(event) => {
        if (event.button !== 0) return;
        touched.current = true;
        event.currentTarget.setPointerCapture(event.pointerId);
        setDragging(true);
        moveTo(event.clientX);
      }}
      onPointerMove={(event) => {
        if (dragging) moveTo(event.clientX);
      }}
      onPointerUp={(event) => {
        event.currentTarget.releasePointerCapture(event.pointerId);
        setDragging(false);
      }}
      onPointerCancel={() => setDragging(false)}
    >
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        key={after.path}
        src={imageUrl(after, 1200)}
        srcSet={imageSrcSet(after)}
        alt={afterAlt}
        className='absolute inset-0 h-full w-full object-cover'
        {...imageProps}
      />
      <div
        className='absolute inset-0'
        style={{ clipPath: `inset(0 ${100 - position}% 0 0)` }}
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={imageUrl(before, 1200)}
          srcSet={imageSrcSet(before)}
          alt={beforeAlt}
          className='absolute inset-0 h-full w-full object-cover'
          {...imageProps}
        />
      </div>

      <span
        className={clsxm(
          'pointer-events-none absolute left-3 top-3 rounded-full bg-[#0b0b13]/55 px-2.5 py-1 text-xs font-semibold text-white backdrop-blur-sm transition-opacity duration-200',
          position < 14 && 'opacity-0',
        )}
      >
        {labels.before}
      </span>
      <span
        className={clsxm(
          'pointer-events-none absolute right-3 top-3 rounded-full bg-white/85 px-2.5 py-1 text-xs font-semibold text-[#10101c] backdrop-blur-sm transition-opacity duration-200',
          position > 86 && 'opacity-0',
        )}
      >
        {labels.after}
      </span>

      <div
        className='pointer-events-none absolute inset-y-0 w-0.5 -translate-x-1/2 bg-white shadow-[0_0_0_1px_rgba(15,15,40,0.12)]'
        style={{ left: `${position}%` }}
      >
        <div
          role='slider'
          tabIndex={0}
          aria-label={sliderLabel}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(position)}
          onKeyDown={(event) => {
            const step = event.shiftKey ? 10 : 4;
            let next: number | null = null;
            if (event.key === 'ArrowLeft') next = position - step;
            if (event.key === 'ArrowRight') next = position + step;
            if (event.key === 'Home') next = 0;
            if (event.key === 'End') next = 100;
            if (next === null) return;
            event.preventDefault();
            touched.current = true;
            setPosition(Math.min(100, Math.max(0, next)));
          }}
          className={clsxm(
            'pointer-events-auto absolute left-1/2 top-1/2 flex h-11 w-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white text-[#10101c] shadow-[0_8px_24px_-6px_rgba(15,15,40,0.45)] ring-1 ring-black/5 transition-transform duration-150',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
            dragging ? 'scale-95' : 'group-hover:scale-105',
          )}
        >
          <svg
            viewBox='0 0 24 24'
            className='h-5 w-5'
            fill='none'
            stroke='currentColor'
            strokeWidth={2}
            strokeLinecap='round'
            strokeLinejoin='round'
            aria-hidden='true'
          >
            <path d='m9 7-5 5 5 5' />
            <path d='m15 7 5 5-5 5' />
          </svg>
        </div>
      </div>
    </div>
  );
}
